// Page frame: sidebar on wide screens, top bar and bottom tabs on phones.
import { Link, useRoute } from '../router.js';
import { useApp } from '../state.js';
import { initialOf } from '../format.js';
import { Icon } from './icons.js';

function navItems(role, unread, doubts, tests) {
  if (role === 'parent') {
    return [
      { href: '/', label: 'Home', icon: 'home', active: (p) => p === '/' || p.startsWith('/children') || p.startsWith('/receipts') },
      { href: '/profile', label: 'Profile', icon: 'user', active: (p) => p === '/profile' || p === '/privacy' },
    ];
  }
  return [
    { href: '/', label: 'Home', icon: 'home', active: (p) => p === '/' || p.startsWith('/batches') || p.startsWith('/classes') },
    { href: '/timetable', label: 'Timetable', icon: 'calendar', active: (p) => p.startsWith('/timetable') },
    { href: '/tests', label: 'Tests', icon: 'tests', badge: tests, active: (p) => p.startsWith('/tests') || p.startsWith('/questions') },
    { href: '/doubts', label: 'Doubts', icon: 'doubt', badge: doubts, active: (p) => p.startsWith('/doubts') },
    { href: '/updates', label: 'Updates', icon: 'megaphone', badge: unread, active: (p) => p.startsWith('/updates') },
    role === 'admin' ? { href: '/admin', label: 'Admin', icon: 'shield', active: (p) => p.startsWith('/admin') || p.startsWith('/receipts') } : null,
    { href: '/profile', label: 'Profile', icon: 'user', active: (p) => p === '/profile' || p === '/fees' || p === '/sharing' || p === '/privacy' || (role !== 'admin' && p.startsWith('/receipts')) },
  ].filter(Boolean);
}

function Badge({ n }) {
  if (!n) return null;
  return <><span class="badge" aria-hidden="true">{n > 9 ? '9+' : n}</span><span class="sr-only">, {n} new</span></>;
}

export function Brand({ small }) {
  return (
    <span class={`brand${small ? ' brand-sm' : ''}`}>
      <span class="mark" aria-hidden="true" />
      <span class="brand-text"><span class="brand-name">Vidyavedh</span><span class="brand-sub">Learning app</span></span>
    </span>
  );
}

export function Shell({ title, backTo, backLabel, actions, children }) {
  const { me, home } = useApp();
  const { path } = useRoute();
  const items = navItems(me && me.role, (home && home.unread_updates) || 0, (home && home.doubts_badge) || 0, (home && home.tests_badge) || 0);
  // Phones: Profile sits in the top bar, so the tabs keep to the places people go every day.
  // Parents have one place to go, so no tab bar.
  const tabs = items.length > 2 ? items.filter((it) => it.href !== '/profile') : [];
  const profile = items.find((it) => it.href === '/profile');
  const initial = initialOf(me && (me.full_name || me.email));
  return (
    <div class={`app${tabs.length ? '' : ' no-tabs'}`}>
      <a class="skip" href="#content">Skip to content</a>
      <aside class="side">
        <Link href="/" class="side-brand"><Brand /></Link>
        <nav aria-label="Main">
          <ul>
            {items.map((it) => (
              <li key={it.href}>
                <Link href={it.href} class={`side-link${it.active(path) ? ' is-active' : ''}`} aria-current={it.active(path) ? 'page' : undefined}>
                  <Icon name={it.icon} /><span>{it.label}</span><Badge n={it.badge} />
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        {me ? <p class="side-me"><span>{me.full_name || me.email}</span><small title={me.email}>{me.email}</small></p> : null}
      </aside>
      <div class="main">
        <header class={`topbar${backTo || actions ? '' : ' topbar-plain'}`}>
          {backTo ? (
            <Link href={backTo} class="back"><Icon name="prev" /><span>{backLabel || 'Back'}</span></Link>
          ) : (
            <Link href="/" class="topbar-brand" aria-label="Vidyavedh home"><Brand small /></Link>
          )}
          {actions ? <div class="topbar-actions">{actions}</div> : null}
          <Link href="/profile" class={`me-btn${profile && profile.active(path) ? ' is-active' : ''}`} aria-label="Profile"
            aria-current={profile && profile.active(path) ? 'page' : undefined}><span aria-hidden="true">{initial}</span></Link>
        </header>
        <main id="content" class="content" tabIndex={-1}>
          {title ? <h1 class="page-title">{title}</h1> : null}
          {children}
        </main>
      </div>
      {tabs.length ? (
        <nav class={`tabbar tabs-${tabs.length}`} aria-label="Main">
          {tabs.map((it) => (
            <Link key={it.href} href={it.href} class={`tab${it.active(path) ? ' is-active' : ''}`} aria-current={it.active(path) ? 'page' : undefined}>
              <span class="tab-icon"><Icon name={it.icon} /><Badge n={it.badge} /></span><span>{it.label}</span>
            </Link>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
